import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Search,
  LayoutDashboard,
  UploadCloud,
  LogOut,
  Monitor,
} from "lucide-react";
import { useStore } from "../../store/useStore";
import { cn } from "../../utils/helpers";

export const CommandPalette = () => {
  const { isCmdPaletteOpen, toggleCmdPalette, setTab, logout } = useStore();
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Keyboard shortcut listener (Cmd+K)
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggleCmdPalette();
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [toggleCmdPalette]);

  const actions = [
    {
      icon: LayoutDashboard,
      label: "Go to Dashboard",
      action: () => setTab("dashboard"),
      shortcut: "G D",
    },
    {
      icon: UploadCloud,
      label: "Upload Data",
      action: () => setTab("upload"),
      shortcut: "G U",
    },
    { icon: LogOut, label: "Log Out", action: logout, shortcut: "⇧ Q" },
  ].filter((item) => item.label.toLowerCase().includes(query.toLowerCase()));

  if (!isCmdPaletteOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[20vh] px-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => toggleCmdPalette(false)}
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: -20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden"
      >
        <div className="flex items-center px-4 border-b border-slate-200 dark:border-slate-800">
          <Search className="text-slate-400" size={20} />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a command or search..."
            className="w-full px-4 py-4 text-lg bg-transparent outline-none text-slate-800 dark:text-slate-100 placeholder:text-slate-400"
          />
          <div className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded text-xs text-slate-500 font-bold border border-slate-200 dark:border-slate-700">
            ESC
          </div>
        </div>

        <div className="max-h-[300px] overflow-y-auto p-2">
          {actions.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">
              No results found.
            </div>
          ) : (
            <>
              <div className="px-2 py-1 text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                Navigation
              </div>
              {actions.map((item, i) => (
                <button
                  key={item.label}
                  onClick={() => {
                    item.action();
                    toggleCmdPalette(false);
                  }}
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-3 rounded-lg text-sm transition-all text-left",
                    i === selectedIndex
                      ? "bg-indigo-600 text-white"
                      : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800",
                  )}
                  onMouseEnter={() => setSelectedIndex(i)}
                >
                  <div className="flex items-center gap-3">
                    <item.icon
                      size={18}
                      className={cn(
                        i === selectedIndex
                          ? "text-indigo-200"
                          : "text-slate-400",
                      )}
                    />
                    <span className="font-medium">{item.label}</span>
                  </div>
                  <div className="flex gap-1">
                    {item.shortcut.split(" ").map((key) => (
                      <kbd
                        key={key}
                        className={cn(
                          "px-1.5 py-0.5 rounded text-[10px] font-bold min-w-[20px] text-center",
                          i === selectedIndex
                            ? "bg-indigo-500 text-white"
                            : "bg-slate-100 dark:bg-slate-800 text-slate-500 border border-slate-200 dark:border-slate-700",
                        )}
                      >
                        {key}
                      </kbd>
                    ))}
                  </div>
                </button>
              ))}
            </>
          )}
        </div>

        <div className="p-2 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 flex justify-between items-center text-[10px] text-slate-500 px-4">
          <span>Pro Tip: Use arrow keys to navigate</span>
          <div className="flex items-center gap-2">
            <Monitor size={10} />
            <span>DDRE System v2.4</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
